// Catalog.ID Client — EFS-Request Mode Handlers
// ============================================================================
// Per-mode dispatch for EFS-originated `registry-request` envelopes. The
// dispatcher in `client.ts` routes here based on `request.requesterId === 'efs'`.
//
// Inbound envelopes have already been signature-verified by the cld-id server's
// relay (`KeyGrantRelay.TryRejectUnverifiedRegistryRequestAsync`). Browser
// code does not re-verify; trust is anchored on the relay's verdict.
//
// v1 modes:
//   - `decapsulate-efs-encrypt`: hybrid X25519 + ML-KEM-1024 decap of a
//     WrappedKey envelope using the recipient's `efs_encrypt` private key.
//     The browser owns the actual decap because the unlocked private key
//     lives in the browser-side `KeyState` (the cld-id .NET service never
//     holds plaintext private keys). Wait-for-unlock semantics (M2c): a locked
//     keybag RAISES THIS TAB (`unlock-gate.ts` sends `focus-request` to the
//     hub) and then awaits `keys.waitForLoad()` within the shared five-minute
//     budget — whatever the sender's `requiresUserInteraction` flag says. The
//     pending decap resolves on the next `keys.load()`.
// ============================================================================

import { keys } from '../state/keys';
import * as aes from '../crypto/aes';
import { kemDecapsulate, HKDF_INFO_EFS_WRAP } from '../crypto/kem';
import { findKemKeyByFingerprint } from './kem-key-chain';
import { sha512 } from '@noble/hashes/sha2.js';
import { recordKeyUse } from '../state/key-use-record';
import type { RequireKeyFn } from './on-demand-key';
import type { EfsRequest, EfsResponse } from './efs-types';
import { ensureUnlocked } from './unlock-gate';

/** Keybag slot holding the account's hybrid X25519 + ML-KEM-1024 EFS key. */
const EFS_ENCRYPT_KEY_ID = 'efs_encrypt';

/**
 * Allowlist of HKDF `info` strings the EFS handler will accept. EFS wraps
 * use the `catalog-efs-wrap-v1` info per EFS WP §12.3; any other value is a
 * domain confusion attempt (e.g. a client trying to coerce the handler into
 * decapsulating a cid-keybag-internal envelope, or a chat epoch wrap, via the
 * EFS path) and is rejected before any private-key material is touched.
 *
 * NEVER extend this list. `catalog-chat-epoch-wrap-v1` in particular belongs
 * to `chat-epoch-handlers.ts` and its own one-entry list: chat has separate
 * keys for compromise scoping, and a shared KDF label gives some of it back.
 */
const ALLOWED_HKDF_INFOS: ReadonlyArray<string> = [
    new TextDecoder().decode(HKDF_INFO_EFS_WRAP),
];

type ResponseBase = Pick<EfsResponse, 'type' | 'to' | 'requestId'>;

export interface EfsDispatchDeps {
    /** Returns the active identity's cid, or null if no identity is active. */
    getActiveCid: () => string | null;
    /** Returns the active session's public handle (`session.sid`), or null. */
    getActiveSessionId: () => string | null;
    /**
     * Resolves the active identity's current `efs_encrypt` public key from the
     * server's public-key surface, split into its classical (X25519) and
     * post-quantum (ML-KEM-1024) halves. Returns null when the identity has no
     * `efs_encrypt` key registered; throws on a transport failure (the handler
     * maps that to `efs-encrypt-lookup-failed`). Sourced from the server, not
     * the local key bag, so `get-active-identity` works on a locked tab.
     */
    getActiveEfsEncryptPublicKey: () => Promise<{ classicalPublicKey: string; pqPublicKey: string } | null>;
    /**
     * Load a key this device does not hold, on demand
     * (`key-grant/on-demand-key.ts`, device-setup-restructure §9.1): prompt for
     * the supersecret, decrypt that one key from the server bag, and offer to
     * keep it here. Wired by `key-grant/client.ts`, which owns the shell the
     * prompt opens.
     *
     * Optional, and its absence is not an error: a build that cannot ask
     * answers `key-not-on-device` instead, which is the same refusal the
     * member sees after dismissing the prompt.
     */
    requireKey?: RequireKeyFn;
    /** Ask the hub to raise the CID tab for this request (M2c). Test seam;
     *  production uses the socket registered by `key-grant/client.ts`. */
    requestFocus?: (requestId: string) => void;
    /** Test seam; production uses the shared
     *  `unlock-gate.UNLOCK_WAIT_TIMEOUT_MS` (5 min). */
    unlockWaitTimeoutMs?: number;
}

export async function dispatchEfsRequest(
    request: EfsRequest,
    deps: EfsDispatchDeps,
): Promise<EfsResponse> {
    const base: ResponseBase = {
        type: 'registry-response',
        to: request.requesterId,
        requestId: request.requestId,
    };

    if (!request._senderServiceId || request._senderServiceId !== request.requesterId) {
        return { ...base, status: 'unavailable', reason: 'sender-mismatch' };
    }
    if (request.requesterId !== 'efs') {
        return { ...base, status: 'unavailable', reason: 'unsupported-requester' };
    }

    const cid = deps.getActiveCid();
    if (!cid) return { ...base, status: 'unavailable', reason: 'no-active-identity' };

    switch (request.mode) {
        case 'decapsulate-efs-encrypt':
            return await handleDecapsulateEfsEncrypt(request, base, cid, deps);
        case 'get-active-identity':
            return await handleGetActiveIdentity(base, deps);
        default:
            return { ...base, status: 'unavailable', reason: 'unsupported-mode' };
    }
}

// ── get-active-identity ─────────────────────────────────────────────────
//
// Discloses the active CID identity's publisher-identity bundle so the EFS
// client-service can populate the `publisher` field of its mutating calls
// (K-prereq-2). Every disclosed field is non-secret: `cid` is a public
// username, `sessionId` is a public session handle (NOT the auth token), and
// the `efs_encrypt` public key is already served by an anonymous endpoint.
// Because the public key is sourced from the server rather than derived from
// the unlocked key bag, this mode succeeds even when the key bag is locked.
async function handleGetActiveIdentity(
    base: ResponseBase,
    deps: EfsDispatchDeps,
): Promise<EfsResponse> {
    // cid is already verified non-null by dispatchEfsRequest; re-checked for
    // type-narrowing.
    const cid = deps.getActiveCid();
    if (!cid) return { ...base, status: 'unavailable', reason: 'no-active-identity' };

    const sessionId = deps.getActiveSessionId();
    if (!sessionId) {
        // An active identity should always carry a session handle; its absence
        // means SessionState was not populated (e.g. a tab mid-bootstrap).
        return { ...base, status: 'unavailable', reason: 'no-session-handle' };
    }

    let efsEncrypt: { classicalPublicKey: string; pqPublicKey: string } | null;
    try {
        efsEncrypt = await deps.getActiveEfsEncryptPublicKey();
    } catch {
        return { ...base, status: 'unavailable', reason: 'efs-encrypt-lookup-failed' };
    }
    if (!efsEncrypt) {
        // The active identity has no efs_encrypt public key registered.
        return { ...base, status: 'unavailable', reason: 'no-efs-encrypt-key' };
    }

    return { ...base, status: 'approved', cid, sessionId, efsEncrypt };
}

// ── decapsulate-efs-encrypt ─────────────────────────────────────────────
//
// Gate stack (post-relay-verification; gates 1–6 already enforced server-side):
//   7. Required fields present (recipientPubkeyDigest, wrappedKey,
//      hkdfInfo, aadBase64, aadDigestBase64).
//   8. `hkdfInfo` is on the EFS allowlist (rejects domain-confusion attempts
//      that would try to decapsulate a cid-keybag-internal envelope via the
//      EFS path).
//   9. Wait-for-unlock (M2c): a locked keybag raises this tab and awaits
//      keys.waitForLoad() within the shared five-minute budget. Returns
//      `cid-locked` only if the member did not unlock within it.
//  10. Find an active or retiring `efs_encrypt` keybag entry whose derived
//      hybrid public key SHA-512-matches `recipientPubkeyDigest`. The chain
//      is walked in full per CID #4 design (no chain-depth bound).
//  11. Recompute SHA-512 of the wrapped-key bytes; compare to
//      `wrappedKeyDigestBase64`. Catches body / canonical-envelope mismatch.
//  12. Recompute SHA-512 of the AAD bytes; compare to `aadDigestBase64`.
//      Same role as gate 11 but for the AAD half of the body.
//  13. `kemDecapsulate(secretKey, wrappedKey, info, aad)`. The AEAD decrypt
//      inside kem.ts is the final witness — a mismatched key, mismatched
//      info, mismatched AAD, or corrupted ciphertext throws uniformly.
async function handleDecapsulateEfsEncrypt(
    request: EfsRequest,
    base: ResponseBase,
    accountCid: string,
    deps: EfsDispatchDeps,
): Promise<EfsResponse> {
    if (!request.recipientPubkeyDigest || !request.wrappedKeyBase64
        || !request.wrappedKeyDigestBase64
        || request.hkdfInfo === undefined
        || request.aadBase64 === undefined
        || request.aadDigestBase64 === undefined) {
        return { ...base, status: 'unavailable', reason: 'missing-required-field' };
    }

    // Gate 8: HKDF info allowlist. Reject any info string this handler is not
    // explicitly authorized to decapsulate under.
    if (!ALLOWED_HKDF_INFOS.includes(request.hkdfInfo)) {
        return { ...base, status: 'unavailable', reason: 'hkdf-info-not-permitted' };
    }

    // Gate 9: the universal unlock redirect (M2c, §10 item 5). A locked bag
    // raises this tab and waits, whatever the sender's flag says — the flag
    // now means only "focus BEFORE delivery because a dialog is coming".
    // `cid-locked` is what the caller hears when the member never unlocked.
    if (!await ensureUnlocked(request, deps)) {
        return { ...base, status: 'unavailable', reason: 'cid-locked' };
    }

    let wrappedKey: Uint8Array;
    try {
        wrappedKey = aes.fromBase64(request.wrappedKeyBase64);
    } catch {
        return { ...base, status: 'unavailable', reason: 'bad-wrapped-key-base64' };
    }

    let aad: Uint8Array;
    try {
        aad = request.aadBase64 === '' ? new Uint8Array(0) : aes.fromBase64(request.aadBase64);
    } catch {
        return { ...base, status: 'unavailable', reason: 'bad-aad-base64' };
    }

    // Gate 11: wrapped-key digest binding. The canonical envelope only commits
    // to the SHA-512 of the wrapped-key bytes; if the body field disagrees
    // with the digest the relay signed off on, refuse. Without this check a
    // relay-verified envelope could be paired with a substituted body.
    const recomputedBlobDigest = sha512(wrappedKey);
    const recomputedBlobDigestB64 = aes.toBase64(recomputedBlobDigest);
    if (recomputedBlobDigestB64 !== request.wrappedKeyDigestBase64) {
        return { ...base, status: 'unavailable', reason: 'wrapped-key-digest-mismatch' };
    }

    // Gate 12: AAD digest binding. Same role as gate 11 for the AAD bytes.
    const recomputedAadDigest = sha512(aad);
    const recomputedAadDigestB64 = aes.toBase64(recomputedAadDigest);
    if (recomputedAadDigestB64 !== request.aadDigestBase64) {
        return { ...base, status: 'unavailable', reason: 'aad-digest-mismatch' };
    }

    // Gate 10: locate the recipient key in the chain (all generations,
    // retiring entries included — see `kem-key-chain.ts`).
    let match = findKemKeyByFingerprint(EFS_ENCRYPT_KEY_ID, request.recipientPubkeyDigest);

    // Gate 10b: ABSENT IS NOT LOCKED (device-setup-restructure §1 item 12). An
    // unlocked bag with no `efs_encrypt` chain at all means the member keeps
    // this key off this device (whitepaper §3.4.7) — not that the wrap is
    // addressed to a generation we lost. Only the former is loadable on
    // demand, and only for a request that refocuses this tab: the loader
    // prompts for the supersecret, and nobody would see that prompt for a
    // background call.
    if (!match && keys.getKeyChain(EFS_ENCRYPT_KEY_ID).length === 0) {
        if (request.requiresUserInteraction === true && deps.requireKey) {
            try {
                await deps.requireKey(EFS_ENCRYPT_KEY_ID, 'open a file stored for you');
            } catch {
                // A failed load is an absent key; the refusal below says so.
            }
            match = findKemKeyByFingerprint(EFS_ENCRYPT_KEY_ID, request.recipientPubkeyDigest);
        }
        if (!match) {
            return { ...base, status: 'unavailable', reason: 'key-not-on-device' };
        }
    }

    if (!match) {
        // The chain IS here and no generation matches — a wrap for a key this
        // account never held, or one rotated away. Unchanged vocabulary.
        return { ...base, status: 'unavailable', reason: 'key-not-in-bag' };
    }

    // Gate 13: decap. kemDecapsulate throws on any failure inside the
    // hybrid construction (wrong key, corrupted ciphertext, bad version
    // byte, mismatched info, mismatched AAD). Surface those uniformly as
    // `decap-failed` so EFS can branch on a single error code rather than
    // guessing the cause.
    const info = new TextEncoder().encode(request.hkdfInfo);
    let plaintext: Uint8Array;
    try {
        plaintext = await kemDecapsulate(match.secretKey, wrappedKey, info, aad);
    } catch (err) {
        const detail = err instanceof Error ? err.message.slice(0, 80) : 'unknown';
        return { ...base, status: 'unavailable', reason: 'decap-failed:' + detail };
    }

    // Evidence of use (§9.4), written only now that the operation has SUCCEEDED
    // and only for the oracle it was: the private key never left this tab.
    recordKeyUse(accountCid, EFS_ENCRYPT_KEY_ID, {
        requesterId: request.requesterId,
        mode: request.mode,
        kind: 'oracle',
    });

    return {
        ...base,
        status: 'approved',
        plaintextBase64: aes.toBase64(plaintext),
    };
}
